import { Button } from '@/components/ui/button';

interface Props {
  shownOffset: number;
  /** Rows actually rendered on this page (may be less than `pageSize` on the
   *  last page). */
  shownCount: number;
  total: number;
  pageSize: number;
  page: number;
  pageCount: number;
  isFetching: boolean;
  onPrevious: () => void;
  onNext: () => void;
}

/**
 * "Showing X–Y of Z" + Previous/Next, for a server-paged list. Hides itself
 * when everything fits on one page — callers don't need to gate on `total`
 * themselves.
 */
export function PaginationFooter({
  shownOffset,
  shownCount,
  total,
  pageSize,
  page,
  pageCount,
  isFetching,
  onPrevious,
  onNext,
}: Props): React.ReactElement | null {
  const isSinglePage = total <= pageSize;
  if (isSinglePage) return null;

  const start = shownOffset + 1;
  const end = Math.min(shownOffset + shownCount, total);
  const canGoPrevious = page > 0 && !isFetching;
  const canGoNext = page + 1 < pageCount && !isFetching;

  return (
    <div className="flex items-center justify-between text-xs text-muted-foreground">
      <span>
        Showing {start}–{end} of {total}
      </span>

      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={!canGoPrevious} onClick={onPrevious}>
          Previous
        </Button>

        <span>
          Page {page + 1} of {pageCount}
        </span>

        <Button size="sm" variant="outline" disabled={!canGoNext} onClick={onNext}>
          Next
        </Button>
      </div>
    </div>
  );
}
